import { Typography } from '@mui/material';
import type { ReactElement } from 'react';

import { convertUnits } from '../../helpers/unitConversion';
import type { PanelInfo, PanelData } from '../../types/monitoringTypes';
import SpinnerLoader from '../Loaders/SpinnerLoader';

interface StatStyles {
  height: number;
  fontSize: string;
}

interface StatProps {
  panelInfo: PanelInfo;
  styles: StatStyles;
  isLoading: boolean;
  panelData?: PanelData;
}

const Stat = ({ panelInfo, styles, isLoading, panelData }: StatProps): ReactElement => {
  const getPanelValues = (): string | number => {
    if (panelData && Object.keys(panelData).length === 0) {
      return 'No data';
    }

    try {
      const { refId } = panelInfo.targets?.find(target => !target.hide) ?? {};
      const frames = refId && panelData?.results[refId]?.frames[0]?.data.values;

      if (!frames || frames.length === 0) {
        return 'No data';
      }

      const lastValue = frames[frames.length - 1]?.[0];

      if (lastValue === undefined) {
        return 'No data';
      }

      return typeof lastValue === 'string'
        ? lastValue
        : convertUnits(lastValue, panelInfo.unit ?? '');
    } catch (error) {
      console.error('Error while getting panel values:', error);
      return 'No data';
    }
  };

  const value = getPanelValues();

  return (
    <SpinnerLoader
      isLoading={isLoading}
      height={styles.height}
      labelText='Loading stat data...'
    >
      <div
        style={{
          height: styles.height,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant='h4'
          color='primary'
          align='center'
          fontSize={styles.fontSize}
        >
          {!isLoading && value}
        </Typography>
      </div>
    </SpinnerLoader>
  );
};

export default Stat;
